import dayjs, { Dayjs } from "dayjs";
import { z } from "zod";
import { refinements } from "@helpers/refinements";
import i18n from "@/i18n";
import { withCustomFields } from "@reusables/BaseCustomFieldsSection/logic";

const ORDER_PRICE_PRECISION = import.meta.env.VITE_ORDER_PRICE_PRECISION;

const validDateRefinement = refinements.validDate;
const maxDecimalPrecisionRefinement = refinements.maxDecimalPrecision(ORDER_PRICE_PRECISION);
const boundedByRefinement = refinements.boundedBy(0, 100);

export const supplierSchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string()
});

const lineSchema = z.object({
    id: z.number().optional(),
    product: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
    }),
    quantity: z.coerce.number().positive(),
    unit_price: z.object({
        converted: z.coerce.number()
            .refine(refinements.positive.refine, refinements.positive.message)
            .refine(maxDecimalPrecisionRefinement.refine, maxDecimalPrecisionRefinement.message),
        original: z.number()
    }),
    discount: z.coerce.number().refine(boundedByRefinement.refine, boundedByRefinement.message).optional(),
    tax: z.object({
        id: z.number(),
        name: z.string().nullish(),
        rate: z.number()
    }).nullish()
});

const groupSchema = z.object({
    id: z.number().nullish(),
    name: z.string(),
    comment: z.string().nullish(),
    module: z.union([z.literal("bom"), z.literal("collection"), z.literal("custom")]),
    module_entity_id: z.number().nullish(),
    lines: z.array(lineSchema)
});

const lineGroupUnion: z.ZodType<LineSchema | GroupSchema> = z.lazy(() =>
    z.union([lineSchema, groupSchema])
);

export const purchaseOrdersScheme = withCustomFields(
    z.object({
        mode: z.union([z.literal("creation"), z.literal("editing")]),
        is_editable: z.boolean(),

        reference_number: z.object({
            id: z.number().nullish(),
            code: z.string().trim().nullish()
        }),

        supplier: supplierSchema,
        purchase_date: z.instanceof(dayjs as unknown as typeof Dayjs).refine(validDateRefinement.refine, validDateRefinement.message),
        preferred_delivery_date: z.instanceof(dayjs as unknown as typeof Dayjs).refine(validDateRefinement.refine, validDateRefinement.message).nullish(),

        our_reference: z.object({
            id: z.number(),
            name: z.string()
        }).nullish(),
        their_reference: z.object({
            id: z.number(),
            name: z.string()
        }).nullish(),
        payment_terms: z.object({
            id: z.number(),
            name: z.string()
        }).nullish(),
        delivery_terms: z.object({
            id: z.number(),
            name: z.string()
        }).nullish(),

        currency: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string(),
            rate: z.number(),
            symbol: z.string(),
            isBase: z.boolean(),
            isFixed: z.boolean()
        }),

        language: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string()
        }).nullish(),

        exchange_rate: z.number().positive(),

        billing_address: z.object({
            name: z.string(),
            street: z.string().nullish(),
            city: z.string().nullish(),
            country: z.object({
                id: z.number(),
                name: z.string(),
                code: z.string()
            }).nullish()
        }),

        delivery_address: z.object({
            id: z.number(),
            name: z.string(),
            street: z.string().nullish(),
            city: z.string().nullish(),
            country: z.object({
                id: z.number(),
                name: z.string(),
                code: z.string()
            }).nullish()
        }).nullish(),

        is_billing_for_delivery: z.boolean(),

        is_split_by_products: z.boolean(),
        shipping_cost: z.coerce.number().optional(),

        lines: z.array(lineGroupUnion).min(1, {
            message: i18n.t("general.validation.global.noProductsAdded")
        }),

        deleted_lines: z.array(z.number().nullish()).nullish()
    }).superRefine((data, ctx) => {
        if (!data.is_billing_for_delivery && !data.delivery_address) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "general.validation.global.required"
                },
                path: ["delivery_address"]
            });
        }

        if (data.preferred_delivery_date && data.preferred_delivery_date.isBefore(data.purchase_date, "day")) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "general.validation.global.invalidPreferredDeliveryDate"
                },
                path: ["preferred_delivery_date"]
            });
        }
    })
);

export type LineGroupUnion = z.infer<typeof lineGroupUnion>;
export type GroupSchema = z.infer<typeof groupSchema>;
export type LineSchema = z.infer<typeof lineSchema>;

export function isGroup(union: LineGroupUnion): union is GroupSchema {
    return union && "module" in union && "name" in union && "lines" in union;
}

export function isLine(union: LineGroupUnion): union is LineSchema {
    return union && "product" in union && "quantity" in union;
}

export type PurchaseOrdersFormTyping = z.infer<typeof purchaseOrdersScheme>;
